import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { Component, computed, inject, Signal, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDivider } from '@angular/material/list';
import { VulcanIconComponent } from '@eagle-i/vulcan-component-library';
import { debounceTime, map, skip, startWith, take } from 'rxjs';
import { GenEiaRegion } from '../../../generated/serverModels/GenEiaRegion';
import { AppService } from '../../../services/app.service';
import { LocationService } from '../../../services/location.service';
import { StateRequestFormModalComponent } from './modals/state-request-form-modal/state-request-form-modal.component';
import { RegionDropdownItem, StateDropdownItem } from './utility/drop-down-items';

@Component({
  selector: 'tgrid-location-selection-dropdown',
  imports: [
    CdkConnectedOverlay,
    CdkOverlayOrigin,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatDivider,
    VulcanIconComponent,
    FormsModule,
  ],
  templateUrl: './location-selection-dropdown.component.html',
  styleUrl: './location-selection-dropdown.component.scss',
  standalone: true,
})
export class LocationSelectionDropdownComponent {
  private locationService = inject(LocationService);
  private appService = inject(AppService);
  private dialog = inject(MatDialog);

  protected readonly panelOpen = signal<boolean>(false);
  protected readonly searchTerm = signal<string>('');

  protected readonly regions: Signal<RegionDropdownItem[]> = toSignal(
    this.locationService.loadRegions().pipe(
      map(lst => lst.map((d: GenEiaRegion) => {
        const region = new RegionDropdownItem(d);
        region.children.forEach(child => child.isSelected.set(true));
        return region;
      })),
      startWith([]),
    ), { initialValue: [] },
  );
  protected readonly searchStates = computed(() => {
    const searchTerm = this.searchTerm().toLowerCase();
    if (!searchTerm) {
      return [];
    } else {
      return this.regions()
        .flatMap((region: RegionDropdownItem) => region.children)
        .filter((state: StateDropdownItem) => state.name.toLowerCase().includes(searchTerm));
    }
  });

  protected readonly selected = computed<StateDropdownItem[]>(() => {
    return this.regions().flatMap((region: RegionDropdownItem) =>
      region.children.filter(state => state.isSelected()),
    );
  });

  protected selectionDisplayString = computed(() => {
    const selectedStates = this.selected();
    if (selectedStates.length === 0) {
      return 'Select Locations';
    } else if (selectedStates.length === 1) {
      return selectedStates[0].name;
    } else {
      return `${selectedStates.length} States`;
    }
  });

  constructor() {
    toObservable(this.regions).pipe(skip(1), take(1)).subscribe((regions) => {
      if (regions.length === 0) {
        this.requestAdditionalStates(true);
      }
    });
    toObservable(this.selected).pipe(
      takeUntilDestroyed(),
      debounceTime(100),
    ).subscribe(() => {
      this.appService.selectedStates.set(this.selected().map(state => state.state));
    });
  }

  protected clear() {
    this.selected().forEach((state) => state.isSelected.set(false));
  }

  protected toggleRegion(region: RegionDropdownItem, checked: boolean) {
    region.children.forEach((state) => state.isSelected.set(checked));
  }

  protected requestAdditionalStates(includeMessage = false) {
    this.panelOpen.set(false);
    this.dialog.open(StateRequestFormModalComponent, {
      width: '550px',
      disableClose: true,
      data: {
        message: includeMessage
          ? 'Your account does not currently have access to any states. Select the applicable state(s) and provide a brief business justification to support access review.'
          : undefined,
      },
    });
  }

}

